import { useState, useEffect } from 'react';
import { Users, Calendar, Eye, FileText, TrendingUp, ArrowUpRight, BarChart3 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { apiRequest } from '../../lib/utils';

export default function AdminDashboard() {
  const [stats, setStats] = useState({
    total_visits: 0,
    total_leads: 0,
    total_appointments: 0,
    visits_by_city: [],
    page_views: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiRequest('/admin/analytics/summary')
      .then(setStats)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const statCards = [
    {
      title: 'Total Visits',
      value: stats.total_visits,
      icon: Eye,
      color: 'bg-blue-500',
      trend: '+12%'
    },
    {
      title: 'Total Leads',
      value: stats.total_leads,
      icon: Users,
      color: 'bg-green-500',
      trend: '+8%'
    },
    {
      title: 'Appointments',
      value: stats.total_appointments,
      icon: Calendar,
      color: 'bg-orange-500',
      trend: '+15%'
    },
    {
      title: 'Blog Posts',
      value: 8,
      icon: FileText,
      color: 'bg-purple-500',
      trend: '0%'
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-orange-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
          Dashboard
        </h1>
        <p className="text-slate-500 mt-1">Welcome back! Here's an overview of your website.</p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, idx) => (
          <Card key={idx} className="border-none shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">{stat.title}</p>
                  <p className="text-3xl font-bold text-slate-900 mt-2">{stat.value}</p>
                  <div className="flex items-center gap-1 mt-2 text-sm">
                    <TrendingUp className="w-4 h-4 text-green-500" />
                    <span className="text-green-600 font-medium">{stat.trend}</span>
                    <span className="text-slate-400">vs last month</span>
                  </div>
                </div>
                <div className={`w-12 h-12 rounded-xl ${stat.color} flex items-center justify-center`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Top Pages */}
        <Card className="border-none shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-orange-600" />
              Top Pages
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats.page_views && stats.page_views.length > 0 ? (
              <div className="space-y-4">
                {stats.page_views.map((page, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <span className="text-slate-600">{page._id || 'Unknown'}</span>
                    <span className="text-slate-900 font-medium">{page.count} views</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">No page view data yet</p>
            )}
          </CardContent>
        </Card>

        {/* Visits by City */}
        <Card className="border-none shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-orange-600" />
              Visits by City
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats.visits_by_city && stats.visits_by_city.length > 0 ? (
              <div className="space-y-4">
                {stats.visits_by_city.map((city, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <span className="text-slate-600">{city._id || 'Unknown'}</span>
                    <span className="text-slate-900 font-medium">{city.count} visits</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">No visitor data yet</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="mt-8">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'View Leads', href: '/admin/leads', icon: Users },
            { label: 'Appointments', href: '/admin/appointments', icon: Calendar },
            { label: 'Manage Blog', href: '/admin/blogs', icon: FileText },
            { label: 'View Analytics', href: '/admin/analytics', icon: BarChart3 },
          ].map((action, idx) => (
            <a
              key={idx}
              href={action.href}
              className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200 hover:border-orange-300 hover:shadow-md transition-all group"
            >
              <div className="w-10 h-10 rounded-lg bg-slate-100 group-hover:bg-orange-100 flex items-center justify-center transition-colors">
                <action.icon className="w-5 h-5 text-slate-600 group-hover:text-orange-600" />
              </div>
              <span className="text-sm font-medium text-slate-700 group-hover:text-orange-600">
                {action.label}
              </span>
              <ArrowUpRight className="w-4 h-4 text-slate-400 group-hover:text-orange-600 ml-auto" />
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
